import { useState, useRef, useEffect } from 'react';
import * as styles from '../styles/components/data-table-menu.css.ts';

interface DataTableMenuProps {
  onAddToReport: () => void;
  onExport?: () => void;
  onViewSQLQuery?: () => void;
  onReloadDataset?: () => void;
  showSQLOption?: boolean;
  showReloadOption?: boolean;
  // Materialized View options
  isMaterialized?: boolean;
  onToggleMaterialize?: () => void;
  showMaterializeOption?: boolean;
  // Edit Mode options
  onStartEditMode?: () => void;
  showEditOption?: boolean;
}

export function DataTableMenu({
  onAddToReport,
  onExport,
  onViewSQLQuery,
  onReloadDataset,
  showSQLOption = false,
  showReloadOption = false,
  isMaterialized = false,
  onToggleMaterialize,
  showMaterializeOption = false,
  onStartEditMode,
  showEditOption = false
}: DataTableMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // メニューの外側をクリックした時に閉じる
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleMenuItemClick = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  return (
    <div className={styles.menuContainer}>
      <button
        ref={buttonRef}
        className={styles.menuButton}
        onClick={() => setIsOpen(!isOpen)}
        title="Table options"
        aria-label="Data table menu"
        data-testid="table-menu-button"
      >
        ⋮
      </button>
      
      {isOpen && (
        <div ref={menuRef} className={styles.menuDropdown}>
          {showEditOption && onStartEditMode && (
            <>
              <button
                className={styles.menuItem}
                onClick={() => handleMenuItemClick(onStartEditMode)}
                data-testid="start-edit-mode"
              >
                ✏️ Edit Data
              </button>
              <div className={styles.menuDivider} />
            </>
          )}
          <button
            className={styles.menuItem}
            onClick={() => handleMenuItemClick(onAddToReport)}
            data-testid="add-to-report"
          >
            📊 Add to Report
          </button>
          {onExport && (
            <button
              className={styles.menuItem}
              onClick={() => handleMenuItemClick(onExport)}
              data-testid="export-data"
            >
              📥 Export
            </button>
          )}
          {showSQLOption && onViewSQLQuery && (
            <button
              className={styles.menuItem}
              onClick={() => handleMenuItemClick(onViewSQLQuery)}
              data-testid="view-sql-query"
            >
              📜 View SQL Query
            </button>
          )}
          {showMaterializeOption && onToggleMaterialize && (
            <>
              <div className={styles.menuDivider} />
              <label
                className={styles.menuItem}
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                data-testid="toggle-materialize"
              >
                <input
                  type="checkbox"
                  checked={isMaterialized}
                  onChange={(e) => {
                    e.stopPropagation();
                    onToggleMaterialize();
                  }}
                  style={{ marginRight: '8px' }}
                />
                💾 Save data with project (Materialized View)
              </label>
            </>
          )}
          {showReloadOption && onReloadDataset && (
            <>
              <div className={styles.menuDivider} />
              <button
                className={styles.menuItem}
                onClick={() => handleMenuItemClick(onReloadDataset)}
                data-testid="reload-dataset"
              >
                🔄 Reload Dataset...
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}